import _lineLength from "./lineLength.js";
const lineLength = _lineLength;
const TEXT_NODE = typeof Node !== "undefined" && Node.TEXT_NODE ? Node.TEXT_NODE : 3;
const ELEMENT_NODE = typeof Node !== "undefined" && Node.ELEMENT_NODE ? Node.ELEMENT_NODE : 1;
function wrapWithFormatAncestors(wordText, textNode, paragraph) {
  const doc = paragraph.ownerDocument;
  let inner = doc.createTextNode(wordText);
  let node = textNode.parentNode;
  while (node && node !== paragraph) {
    if (node.nodeType === ELEMENT_NODE && node.nodeName !== "BR") {
      const clone = node.cloneNode(false);
      clone.appendChild(inner);
      inner = clone;
    }
    node = node.parentNode;
  }
  return inner;
}
function isEffectivelyEmpty(node) {
  if (!node) {
    return true;
  }
  if (node.textContent && node.textContent.trim()) {
    return false;
  }
  if (typeof node.querySelector === "function" && node.querySelector("br, img")) {
    return false;
  }
  return true;
}
function pruneEmptyAncestors(textNode, paragraph) {
  let node = textNode;
  while (node && node !== paragraph) {
    const parent = node.parentNode;
    if (!parent) {
      break;
    }
    parent.removeChild(node);
    if (parent === paragraph) {
      break;
    }
    if (!isEffectivelyEmpty(parent)) {
      break;
    }
    node = parent;
  }
}
function collectAllTextNodes(paragraph) {
  const textNodes = [];
  function traverse(node) {
    if (node.nodeType === TEXT_NODE) {
      textNodes.push(node);
    } else if (node.nodeName !== "RT") {
      node.childNodes.forEach(child => traverse(child));
    }
  }
  traverse(paragraph);
  return textNodes;
}
function getWords(element) {
  const text = lineLength.normalizeLine(element.textContent);
  if (!text) {
    return [];
  }
  return text.split(/\s+/);
}
function peekEdgeWord(paragraph, fromEnd) {
  const words = getWords(paragraph);
  if (words.length === 0) {
    return null;
  }
  const wordText = fromEnd ? words[words.length - 1] : words[0];
  const remainingText = (fromEnd ? words.slice(0, -1) : words.slice(1)).join(" ");
  return {
    wordText,
    remainingText,
    wordCount: words.length
  };
}

/*
    Remove the first/last word of a paragraph and return it as a fragment that keeps the
    formatting of every character. A word can span several text nodes (e.g. foo<em>bar</em>),
    so we walk text nodes from the edge until we reach whitespace.
*/
function extractEdgeWord(paragraph, fromEnd) {
  const doc = paragraph.ownerDocument;
  const textNodes = collectAllTextNodes(paragraph);
  if (fromEnd) {
    textNodes.reverse();
  }
  const pieces = [];
  let started = false;
  for (const node of textNodes) {
    const raw = node.data;
    //Skip whitespace at the edge of the line before the word starts
    const edgeSpace = started ? "" : fromEnd ? raw.match(/\s*$/)[0] : raw.match(/^\s*/)[0];
    const body = fromEnd ? raw.slice(0, raw.length - edgeSpace.length) : raw.slice(edgeSpace.length);
    if (!body) {
      continue;
    }
    const wordPart = fromEnd ? body.match(/\S*$/)[0] : body.match(/^\S*/)[0];
    if (wordPart) {
      started = true;
      pieces.push({
        node,
        text: wordPart
      });
      node.data = fromEnd ? body.slice(0, body.length - wordPart.length) : body.slice(wordPart.length);
    }
    if (wordPart.length < body.length) {
      //Reached whitespace inside this node, the word is complete
      break;
    }
  }
  if (pieces.length === 0) {
    return null;
  }
  if (fromEnd) {
    pieces.reverse();
  }
  const fragment = doc.createDocumentFragment();
  pieces.forEach(({
    node,
    text
  }) => {
    fragment.appendChild(wrapWithFormatAncestors(text, node, paragraph));
  });
  pieces.forEach(({
    node
  }) => {
    if (!node.data && node.parentNode) {
      pruneEmptyAncestors(node, paragraph);
    }
  });
  return {
    fragment,
    wordText: pieces.map(piece => piece.text).join("")
  };
}
function appendWord(paragraph, fragment) {
  if (paragraph.textContent.trim()) {
    paragraph.appendChild(paragraph.ownerDocument.createTextNode(" "));
  }
  paragraph.appendChild(fragment);
}
function prependWord(paragraph, fragment) {
  const doc = paragraph.ownerDocument;
  if (paragraph.firstChild) {
    paragraph.insertBefore(doc.createTextNode(" "), paragraph.firstChild);
    paragraph.insertBefore(fragment, paragraph.firstChild);
  } else {
    paragraph.appendChild(fragment);
  }
}
function createParagraphFromFragment(sourceParagraph, fragment) {
  const paragraph = sourceParagraph.ownerDocument.createElement("p");
  paragraph.className = sourceParagraph.className;
  paragraph.appendChild(fragment);
  return paragraph;
}
function displayLength(element) {
  return lineLength.normalizeLine(element.textContent).length;
}
function movedLineLength(destinationLength, wordText) {
  return destinationLength + wordText.length + (destinationLength > 0 ? 1 : 0);
}
export default {
  getWords,
  peekFirstWord: paragraph => peekEdgeWord(paragraph, false),
  peekLastWord: paragraph => peekEdgeWord(paragraph, true),
  extractFirstWord: paragraph => extractEdgeWord(paragraph, false),
  extractLastWord: paragraph => extractEdgeWord(paragraph, true),
  appendWord,
  prependWord,
  createParagraphFromFragment,
  displayLength,
  movedLineLength
};